import { useState, useEffect } from 'react';
import PeopleIcon from '@mui/icons-material/People';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import axios from 'axios';
// @mui
import {
  Card,
  Table,
  Stack,
  Button,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  Grid,
  Box,
  CardHeader,
  CardContent,
  Paper,
} from '@mui/material';
import { ThreeDots } from 'react-loader-spinner';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
// components
import Label from '../../../components/label';
import Iconify from '../../../components/iconify';
import Scrollbar from '../../../components/scrollbar';
import { fNumber } from '../../../utils/formatNumber';
// sections
import { UserListHead } from '../../../sections/@dashboard/user';

import { Page } from '../../../components';
import { getUsers } from '../store/userActions';
import { ConfirmCompleteDialog, EditUser, UsersFilter } from '../components';
import call from '../../../core/services/http';
import { BASE_URL } from '../../../core/environment/environment';
import { getToken } from '../../../shared/auth/authService';
import userConstants from '../store/userConstants';

// ----------------------------------------------------------------------
const TABLE_HEAD = [
  { id: 'display_name', label: 'Name', alignRight: false },
  { id: 'mobile_number', label: 'Mobile#', alignRight: false },
  { id: 'email', label: 'Email', alignRight: false },
  { id: 'department', label: 'Department', alignRight: false },
  { id: 'location', label: 'Branch', alignRight: false },
  { id: 'role', label: 'Role', alignRight: false },
  { id: 'status', label: 'Status', alignRight: false },
  { id: '' },
];

const toastrSuccess = (msg) => {
  toast.success(msg);
};

const toastrError = (msg) => {
  toast.error(msg);
};

const storedUser = JSON.parse(localStorage.getItem('profile'));
const adminRoles = ['admin'];

const isAdmin = storedUser?.role && adminRoles.includes(storedUser.role);
export default function Users() {
  const dispatch = useDispatch();
  const { isLoading, users, pagination } = useSelector((state) => state.userReducer);

  const [open, setOpen] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});
  const [isExporting, setIsExporting] = useState(false);

  const [page, setPage] = useState(0);

  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleClose = () => {
    setOpen(!open);
  };

  const handleCloseEdit = () => {
    setOpenEdit(!openEdit);
    setTimeout(() => {
      const payload = {
        page: page + 1,
        rowsPerPage,
      };
      dispatch(getUsers(payload));
    }, 2000);
  };
  const handleCloseDelete = () => {
    setOpenDelete(!openDelete);
    setTimeout(() => {
      const payload = {
        page: page + 1,
        rowsPerPage,
      };
      dispatch(getUsers(payload));
    }, 2000);
  };
  const downloadPDF = async () => {
    try {
      setIsExporting(true);
      const token = getToken();

      if (!token) {
        toastrError('Authentication required. Please log in again.');
        setIsExporting(false);
        return;
      }

      // Try the reports endpoint first, then fallback to staff endpoint
      let response;
      let endpointUsed = '';

      try {
        // Primary endpoint: reports/staff/export
        endpointUsed = 'reports endpoint';
        response = await axios.get(`${BASE_URL}${userConstants.EXPORT_STAFF('landscape')}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          responseType: 'blob',
          timeout: 30000, // 30 second timeout
        });
      } catch (firstError) {
        console.log('Primary endpoint failed, trying fallback:', firstError.message);

        try {
          // Fallback endpoint: staff/export
          endpointUsed = 'fallback endpoint';
          response = await axios.get(`${BASE_URL}staff/export/`, {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            responseType: 'blob',
            timeout: 30000,
          });
        } catch (secondError) {
          console.error('Both endpoints failed:', secondError.message);
          throw secondError;
        }
      }

      if (response && response.status === 200 && response.data) {
        console.log(`PDF Response from ${endpointUsed}:`, {
          status: response.status,
          dataType: typeof response.data,
          dataSize: response.data.size || response.data.length,
          contentType: response.headers['content-type'],
        });

        // Validate blob response
        if (!(response.data instanceof Blob)) {
          console.error('Response data is not a blob:', response.data);
          toastrError('Invalid PDF data received from server');
          setIsExporting(false);
          return;
        }

        // Check if blob has content
        if (response.data.size === 0) {
          console.error('Received empty blob');
          toastrError('Received empty PDF file');
          setIsExporting(false);
          return;
        }

        // Create blob with proper PDF MIME type
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);

        // Generate filename with timestamp
        const now = new Date();
        const timestamp = now.toISOString().replace(/[:.]/g, '-').split('T')[0];
        const timeString = now.toTimeString().split(' ')[0].replace(/:/g, '-');
        const filename = `users_export_${timestamp}_${timeString}.pdf`;

        // Create and trigger download
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 1000);

        setIsExporting(false);
        toastrSuccess(`PDF exported successfully as ${filename}`);
      } else {
        console.error('Unexpected response:', response?.status, response?.statusText);
        toastrError(`Failed to generate PDF: ${response?.statusText || 'Unknown error'}`);
        setIsExporting(false);
      }
    } catch (error) {
      console.error('PDF Download Error:', error);
      setIsExporting(false);

      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        toastrError('PDF generation timed out. Please try again.');
      } else if (error.response) {
        const { status } = error.response;
        const errorData = error.response.data;

        let errorMessage = 'An error occurred while generating PDF report';

        if (errorData instanceof Blob) {
          // Try to read error message from blob
          try {
            const text = await errorData.text();
            const parsed = JSON.parse(text);
            errorMessage = parsed.message || parsed.error || errorMessage;
          } catch (parseError) {
            console.log('Could not parse error blob:', parseError);
          }
        } else if (errorData && errorData.message) {
          errorMessage = errorData.message;
        }

        switch (status) {
          case 400:
            toastrError(`Bad Request: ${errorMessage}`);
            break;
          case 401:
            toastrError('Unauthorized. Please log in again.');
            break;
          case 403:
            toastrError('Access denied. You do not have permission to export users.');
            break;
          case 404:
            toastrError('PDF export service not found. Please contact support.');
            break;
          case 500:
            toastrError('Server error occurred while generating PDF. Please try again later.');
            break;
          default:
            toastrError(`Error ${status}: ${errorMessage}`);
        }
      } else if (error.request) {
        toastrError('Network error. Please check your connection and try again.');
      } else {
        toastrError('An unexpected error occurred. Please try again.');
      }
    }
  };
  useEffect(() => {
    const payload = {
      page: page + 1,
      rowsPerPage,
    };
    dispatch(getUsers(payload));
  }, [dispatch, page, rowsPerPage]);

  return (
    <>
      <Page title="Users">
        {open && <UsersFilter open={setOpen} handleClose={handleClose} />}
        {openEdit && <EditUser open={openEdit} handleClose={handleCloseEdit} currentStaff={selectedRow} />}
        {openDelete && (
          <ConfirmCompleteDialog open={openDelete} handleClose={handleCloseDelete} staffId={selectedRow?.id} />
        )}
        <Container maxWidth="xl">
          <Grid container spacing={3}>
            <Grid item xs={12} md={4} lg={4}>
              <Paper elevation={3} sx={{ py: 2, textAlign: 'center' }}>
                <Box sx={{ mb: 0.5 }}>
                  <PeopleIcon />
                </Box>
                <Typography variant="h6">{fNumber(pagination?.count)}</Typography>
                <Typography variant="body2">Users</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12}>
              <Card>
                <Box sx={{ p: 2 }}>
                  <Stack direction="row" sx={{ float: 'right' }} spacing={0.5}>
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      onClick={() => downloadPDF()}
                      // startIcon={<FilterListOutlinedIcon />}
                    >
                      Download PDF
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      color="warning"
                      onClick={handleClose}
                      startIcon={<FilterListOutlinedIcon />}
                    >
                      Filter Users
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      component={RouterLink}
                      to={'/dashboard/people/new'}
                      startIcon={<Iconify icon="eva:plus-fill" />}
                    >
                      New User
                    </Button>
                  </Stack>
                </Box>
                <CardHeader title={'People'} subheader={'Staff'} />
                <CardContent>
                  <Scrollbar>
                    <TableContainer sx={{ minWidth: 800 }} component={Paper}>
                      {isLoading ? (
                        <Grid container sx={{ justifyContent: 'center' }}>
                          <ThreeDots
                            height="80"
                            width="80"
                            radius="9"
                            color="#2065d1"
                            ariaLabel="three-dots-loading"
                            wrapperStyle={{}}
                            wrapperClassName=""
                          />
                        </Grid>
                      ) : (
                        <Table size="medium">
                          <UserListHead headLabel={TABLE_HEAD} rowCount={users?.length} />
                          <TableBody>
                            {users?.length > 0 ? (
                              users?.map((user) => (
                                <TableRow
                                  hover
                                  key={user?.id}
                                  tabIndex={-1}
                                  role="checkbox"
                                  // selected={users}
                                >
                                  <TableCell component="th" scope="row" padding="checkbox" sx={{ pl: 2 }}>
                                    <Stack direction="row" alignItems="center" spacing={2}>
                                      <Typography variant="subtitle2" noWrap>
                                        {user?.display_name}
                                      </Typography>
                                    </Stack>
                                  </TableCell>

                                  <TableCell align="left">{user?.mobile_number}</TableCell>
                                  <TableCell align="left">{user?.email}</TableCell>
                                  <TableCell align="left">{user?.department || '—'}</TableCell>
                                  <TableCell align="left">{user?.location?.name}</TableCell>
                                  <TableCell align="left">{user?.role}</TableCell>
                                  <TableCell align="left">
                                    <Label color={(user?.is_active && 'success') || 'error'}>
                                      {(user?.is_active && 'Active') || 'Inactive'}
                                    </Label>
                                  </TableCell>
                                  <TableCell>
                                    <Stack spacing={2} direction="row">
                                      <>
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          onClick={() => {
                                            setSelectedRow(user);
                                            setOpenEdit(true);
                                          }}
                                        >
                                          Edit
                                        </Button>
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          color="error"
                                          onClick={() => {
                                            setSelectedRow(user);
                                            setOpenDelete(true);
                                          }}
                                        >
                                          Delete
                                        </Button>
                                      </>
                                    </Stack>
                                  </TableCell>
                                </TableRow>
                              ))
                            ) : (
                              <>No users found</>
                            )}
                          </TableBody>
                        </Table>
                      )}
                    </TableContainer>
                  </Scrollbar>

                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50, 100]}
                    component="div"
                    count={pagination?.count || 0}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Page>
    </>
  );
}
